import React, { useEffect, useState } from 'react';
import { Layout, Typography, Button, Table, Input, Select, Modal, DatePicker, Popconfirm } from 'antd';
import {
    DeleteOutlined,
    CalendarOutlined,
    SearchOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import useCampaignsStore from '../../stores/useCampaignsStore';
import CampaignViewHeader from './shared/CampaignViewHeader';
import CampaignStatsCards from './shared/CampaignStatsCards';
import CampaignDetailsCard from './shared/CampaignDetailsCard';
import CampaignFlowSection from './shared/CampaignFlowSection';
import CampaignSchedulingDetails from './shared/CampaignSchedulingDetails';
import CampaignContactDrawer from './CampaignContactDrawer';
import PhoneDisplay from '../common/PhoneDisplay';
import { getSupportedTimeZones } from '../../reducers/campaign/resources';
import { formatSchedule } from '../../utils/campaign-timezone';
import useFlowEnrollmentStore from '../../stores/useFlowEnrollmentStore';
import useModuleGroupStore from '../../stores/useModuleGroupStore';

const { Content } = Layout;
const { Text } = Typography;

const compactStyles = {
    page: {
        padding: '8px 12px 12px',
        background: 'var(--gh-gray-50, #FAFAFA)'
    },
    shell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6
    },
    panel: {
        background: '#fff',
        border: '1px solid var(--gh-gray-200, #E6E6E6)',
        borderRadius: 8
    },
    controlsRow: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginTop: 6,
        marginBottom: 6
    }
};

const CampaignViewScheduled = ({ match, history }) => {
    const { campaignId } = match.params;
    const {
        campaignDetails,
        campaignState,
        campaignContacts,
        contactsPagination,
        contactsFilters,
        contactsLoading,
        setContactsFilters,
        setContactsPage,
        fetchCampaignDetails,
        fetchCampaignContacts,
        rescheduleCampaign,
        deleteCampaign: deleteCampaignAction
    } = useCampaignsStore();

    const [localSearch, setLocalSearch] = useState(contactsFilters.search);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [newSchedule, setNewSchedule] = useState(null);
    const [newTimezone, setNewTimezone] = useState('');
    const [timezones, setTimezones] = useState([]);
    const [countdown, setCountdown] = useState('');
    const [drawerContactId, setDrawerContactId] = useState(null);
    const [drawerEnrollment, setDrawerEnrollment] = useState(null);

    const { boardData } = useFlowEnrollmentStore();
    const { modules } = useModuleGroupStore();

    const boardKey = campaignDetails?.modulegroup_id
        ? (campaignId ? `${campaignDetails.modulegroup_id}:${campaignId}` : campaignDetails.modulegroup_id)
        : null;
    const enrollments = boardKey ? (boardData[boardKey] || []) : [];

    const handleContactRowClick = (record) => {
        const enrollment = enrollments.find((e) => e.contact_id === record.contact_id);
        setDrawerEnrollment(enrollment || null);
        setDrawerContactId(record.contact_id);
    };

    const handleDrawerClose = () => {
        setDrawerContactId(null);
        setDrawerEnrollment(null);
    };

    useEffect(() => {
        if (campaignId) {
            fetchCampaignContacts(campaignId);
        }
    }, [campaignId]);

    // Load timezones for reschedule modal
    useEffect(() => {
        getSupportedTimeZones().then(res => {
            setTimezones(res.data || []);
        }).catch(() => {});
    }, []);

    // Countdown timer
    useEffect(() => {
        if (campaignState !== 'scheduled' || !campaignDetails?.schedule) return;

        const update = () => {
            const scheduleTime = moment(campaignDetails.schedule);
            const now = moment();
            const diff = scheduleTime.diff(now);

            if (diff <= 2 * 60 * 1000) {
                setCountdown('soon');
                return;
            }

            const duration = moment.duration(diff);
            const days = Math.floor(duration.asDays());
            const hours = duration.hours();
            const mins = duration.minutes();

            const parts = [];
            if (days > 0) parts.push(`${days}d`);
            if (hours > 0) parts.push(`${hours}h`);
            parts.push(`${mins}m`);
            setCountdown(parts.join(' '));
        };

        update();
        const timer = setInterval(update, 60000);
        return () => clearInterval(timer);
    }, [campaignState, campaignDetails?.schedule]);

    // Debounced search
    useEffect(() => {
        setLocalSearch(contactsFilters.search);
    }, [contactsFilters.search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== contactsFilters.search) {
                setContactsFilters({ search: localSearch });
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [localSearch]);

    const handleTableChange = (pag, filters, sorter) => {
        if (pag.current !== contactsPagination.page || pag.pageSize !== contactsPagination.pageSize) {
            setContactsPage(pag.current, pag.pageSize);
        }
        if (sorter.field && sorter.order) {
            const order = sorter.order === 'ascend' ? 'asc' : 'desc';
            if (sorter.field !== contactsFilters.sort || order !== contactsFilters.order) {
                setContactsFilters({ sort: sorter.field, order });
            }
        }
    };

    const handleReschedule = async () => {
        if (!newSchedule || newSchedule.isBefore(dayjs())) return;
        await rescheduleCampaign(
            campaignId,
            newSchedule.toISOString(),
            newTimezone || campaignDetails.timezone
        );
        setRescheduleOpen(false);
    };

    const handleDelete = async () => {
        await deleteCampaignAction(campaignId);
        history.push('/campaign');
    };

    if (!campaignDetails) return null;

    const { stats = {} } = campaignDetails;
    const contactCount = campaignDetails.contact_count_at_schedule || stats.total || 0;

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            width: 260,
            ellipsis: true,
            render: (text, record) => (
                <a
                    className="gh-table-link"
                    onClick={(e) => { e.stopPropagation(); handleContactRowClick(record); }}
                    title={text}
                    style={{
                        cursor: 'pointer',
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle'
                    }}
                >
                    {text}
                </a>
            )
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile',
            sorter: true,
            render: (text) => <PhoneDisplay value={text} />
        }
    ];

    const extraActions = (
        <>
            <Button
                size="small"
                icon={<CalendarOutlined />}
                onClick={() => {
                    setNewSchedule(campaignDetails.schedule ? dayjs(campaignDetails.schedule) : null);
                    setNewTimezone(campaignDetails.timezone || '');
                    setRescheduleOpen(true);
                }}
            >
                Reschedule
            </Button>
            <Popconfirm
                title="Cancel this campaign?"
                description="This will permanently delete the campaign."
                onConfirm={handleDelete}
                okText="Yes, cancel"
                okType="danger"
            >
                <Button size="small" danger icon={<DeleteOutlined />}>
                    Cancel
                </Button>
            </Popconfirm>
        </>
    );

    return (
        <Layout style={compactStyles.page}>
            <Content style={compactStyles.shell}>
                <CampaignViewHeader
                    campaignDetails={campaignDetails}
                    onBack={() => history.push('/campaign')}
                    onRefresh={() => {
                        fetchCampaignDetails(campaignId);
                        fetchCampaignContacts(campaignId);
                    }}
                    extraActions={extraActions}
                />

                {/* Schedule banner */}
                <div style={{
                    ...compactStyles.panel,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: 'var(--gh-primary-tint, #E8F5E9)'
                }}>
                    <CalendarOutlined style={{ fontSize: 20, color: 'var(--gh-primary-active, #2E7D32)' }} />
                    <div>
                        <Text strong style={{ fontSize: 14, color: 'var(--gh-text-primary, #263238)' }}>
                            {countdown === 'soon' ? 'Scheduled to send soon...' : `Scheduled to send in ${countdown}`}
                        </Text>
                        <div style={{ fontSize: 12, color: 'var(--gh-text-secondary, #607D8B)' }}>
                            {formatSchedule(campaignDetails.schedule, campaignDetails.timezone)}
                            {campaignDetails.timezone ? ` (${campaignDetails.timezone})` : ''}
                        </div>
                    </div>
                </div>

                <div style={{ ...compactStyles.panel, padding: '8px 10px' }}>
                    <CampaignStatsCards
                        stats={{
                            total: contactCount,
                            delivered: 0,
                            failed: 0,
                            pending: contactCount
                        }}
                    />

                    <CampaignDetailsCard
                        campaignDetails={campaignDetails}
                        hideTemplate
                        hideCampaignStatus
                        hideMessage={!!campaignDetails.modulegroup_id}
                    />
                </div>

                {campaignDetails.modulegroup_id && (
                    <CampaignFlowSection
                        moduleGroupId={campaignDetails.modulegroup_id}
                        campaignState="scheduled"
                        defaultExpanded={true}
                        flowSnapshot={campaignDetails.flow_snapshot}
                        moduleResponseOverrides={campaignDetails.module_response_overrides}
                        noSlotsMessage={campaignDetails.no_slots_message}
                    />
                )}

                <CampaignSchedulingDetails
                    availabilitySettings={campaignDetails.availability_settings}
                    flowSnapshot={campaignDetails.flow_snapshot}
                    defaultExpanded={true}
                    schedulingUserId={campaignDetails.scheduling_user_id}
                    schedulingUserIds={campaignDetails.scheduling_user_ids}
                    schedulingStrategy={campaignDetails.scheduling_strategy}
                    schedulingUsers={campaignDetails.scheduling_users}
                    creatorName={campaignDetails.creator_name || campaignDetails.username}
                />

                {/* Contacts preview */}
                <div style={{ ...compactStyles.panel, padding: '0 10px 8px' }}>
                    <div style={compactStyles.controlsRow}>
                        <Input
                            placeholder="Search contacts..."
                            style={{ width: 260 }}
                            value={localSearch}
                            onChange={e => setLocalSearch(e.target.value)}
                            allowClear
                            size="small"
                            prefix={<SearchOutlined style={{ color: 'var(--gh-text-secondary, #607D8B)' }} />}
                        />
                    </div>
                    <Table
                        className="gh-themed-table"
                        columns={columns}
                        dataSource={campaignContacts}
                        rowKey="contact_id"
                        loading={contactsLoading}
                        size="small"
                        onRow={(record) => ({
                            onClick: () => handleContactRowClick(record),
                            style: { cursor: 'pointer' },
                        })}
                        pagination={{
                            current: contactsPagination.page,
                            pageSize: contactsPagination.pageSize,
                            total: contactsPagination.total,
                            showSizeChanger: true,
                            size: 'small',
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                        }}
                        onChange={handleTableChange}
                    />
                </div>

                <CampaignContactDrawer
                    open={!!drawerContactId}
                    onClose={handleDrawerClose}
                    contactId={drawerContactId}
                    enrollment={drawerEnrollment}
                    modulegroupId={campaignDetails.modulegroup_id}
                    campaignId={campaignId}
                    modules={modules}
                    flowSnapshot={campaignDetails.flow_snapshot}
                    moduleResponseOverrides={campaignDetails.module_response_overrides}
                />

                {/* Reschedule modal */}
                <Modal
                    title="Reschedule Campaign"
                    open={rescheduleOpen}
                    onOk={handleReschedule}
                    onCancel={() => setRescheduleOpen(false)}
                    okText="Reschedule"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
                        <div>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>New Date & Time</Text>
                            <DatePicker
                                showTime={{ use12Hours: true, format: "h:mm a" }}
                                format="YYYY-MM-DD h:mm a"
                                style={{ width: '100%' }}
                                value={newSchedule}
                                onChange={setNewSchedule}
                                disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
                                disabledTime={(current) => {
                                    if (current && current.isSame(dayjs(), 'day')) {
                                        const nowHour = dayjs().hour();
                                        const nowMinute = dayjs().minute();
                                        return {
                                            disabledHours: () => [...Array(24).keys()].filter(h => h < nowHour),
                                            disabledMinutes: (selectedHour) => {
                                                if (selectedHour === nowHour) {
                                                    return [...Array(60).keys()].filter(m => m < nowMinute);
                                                }
                                                return [];
                                            }
                                        };
                                    }
                                    return {};
                                }}
                            />
                        </div>
                        <div>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>Timezone</Text>
                            <Select
                                showSearch
                                style={{ width: '100%' }}
                                value={newTimezone || undefined}
                                placeholder="Timezone"
                                onChange={setNewTimezone}
                                filterOption={(input, option) =>
                                    (option?.label || '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={(timezones || []).map(tz => ({
                                    value: tz.value,
                                    label: tz.name,
                                }))}
                            />
                        </div>
                    </div>
                </Modal>
            </Content>
        </Layout>
    );
};

export default CampaignViewScheduled;
